// Keep in sync with server/src/utils/condition-mapper.js and chrome-extension/utils/condition-mapper.js

/**
 * Condition Mapper Utility
 *
 * Single source of truth for all condition mapping across platforms.
 * Converts between canonical Voolist condition values and platform-specific values.
 */

// ==================== CANONICAL CONDITIONS ====================

export const CONDITIONS = Object.freeze({
  NEW: 'NEW',
  LIKE_NEW: 'LIKE_NEW',
  NEW_WITH_DEFECTS: 'NEW_WITH_DEFECTS',
  USED_EXCELLENT: 'USED_EXCELLENT',
  USED_VERY_GOOD: 'USED_VERY_GOOD',
  USED_GOOD: 'USED_GOOD',
  USED_ACCEPTABLE: 'USED_ACCEPTABLE',
  REFURBISHED: 'REFURBISHED',
  FOR_PARTS: 'FOR_PARTS'
});

// ==================== PLATFORMS ====================

export const PLATFORMS = Object.freeze({
  DEPOP: 'depop',
  GRAILED: 'grailed',
  OFFERUP: 'offerup',
  VESTIAIRE: 'vestiaire',
  POSHMARK: 'poshmark',
  VINTED: 'vinted',
  EBAY_TRADING: 'ebay_trading',    // Trading API — output is numeric ID string e.g. "1000"
  EBAY_INVENTORY: 'ebay_inventory',  // Inventory API — output is enum string e.g. "NEW"
  BIGCOMMERCE: 'bigcommerce',
  WOOCOMMERCE: 'woocommerce',     // no condition field
  SHOPIFY: 'shopify',         // no condition field
  ETSY: 'etsy'             // condition derived from when_made
});

// Platforms that have no condition field
const NO_CONDITION_PLATFORMS = new Set([
  PLATFORMS.WOOCOMMERCE,
  PLATFORMS.SHOPIFY
]);

// ==================== IMPORT MAPS (Platform → Canonical) ====================

const FROM_DEPOP = {
  'brand_new': CONDITIONS.NEW,
  'used_like_new': CONDITIONS.LIKE_NEW,
  'used_excellent': CONDITIONS.USED_EXCELLENT,
  'used_good': CONDITIONS.USED_GOOD,
  'used_fair': CONDITIONS.USED_ACCEPTABLE
};

// Grailed's four conditions, as its sell form offers them. `is_not_specified` is only Grailed's own
// default for a listing nobody graded, so it maps to nothing.
const FROM_GRAILED = {
  'is_new': CONDITIONS.NEW,
  'is_gently_used': CONDITIONS.USED_EXCELLENT,
  'is_used': CONDITIONS.USED_GOOD,
  'is_worn': CONDITIONS.USED_ACCEPTABLE
};

// OfferUp's conditions. A listing is written with OfferUp's word (NEW, REFURBISHED, OPEN_BOX, USED, BROKEN,
// OTHER) and read back as a number (100, 80, 60, 40, 20, 0; OFFERUP_API_REFERENCE.md §5): both read here.
// Open Box is unused but opened, so like new; "Other" says nothing about the item and maps to nothing.
const FROM_OFFERUP = {
  'new':         CONDITIONS.NEW,
  '100':         CONDITIONS.NEW,
  'refurbished': CONDITIONS.REFURBISHED,
  '80':          CONDITIONS.REFURBISHED,
  'open_box':    CONDITIONS.LIKE_NEW,
  '60':          CONDITIONS.LIKE_NEW,
  'used':        CONDITIONS.USED_GOOD,
  '40':          CONDITIONS.USED_GOOD,
  'broken':      CONDITIONS.FOR_PARTS,
  '20':          CONDITIONS.FOR_PARTS
};

// Vestiaire's conditions, by its label (what Voolist stores and the extension resolves on Vestiaire's form) or by
// its id on that form (VESTIAIRE_API_REFERENCE.md §5). "Never worn" without a tag is new without tags, as on
// Poshmark.
const FROM_VESTIAIRE = {
  'never worn, with tag': CONDITIONS.NEW,
  '10':                   CONDITIONS.NEW,
  'never worn':           CONDITIONS.LIKE_NEW,
  '9':                    CONDITIONS.LIKE_NEW,
  'very good condition':  CONDITIONS.USED_VERY_GOOD,
  '3':                    CONDITIONS.USED_VERY_GOOD,
  'good condition':       CONDITIONS.USED_GOOD,
  '4':                    CONDITIONS.USED_GOOD,
  'fair condition':       CONDITIONS.USED_ACCEPTABLE,
  '5':                    CONDITIONS.USED_ACCEPTABLE
};

const FROM_POSHMARK = {
  'nwt': CONDITIONS.NEW,
  'uln': CONDITIONS.LIKE_NEW,
  'ug': CONDITIONS.USED_GOOD,
  'uf': CONDITIONS.USED_ACCEPTABLE,
  // Legacy codes (for importing old listings that may still have these)
  'nwot': CONDITIONS.LIKE_NEW,
  'nwob': CONDITIONS.LIKE_NEW,
  'euc': CONDITIONS.USED_EXCELLENT,
  'guc': CONDITIONS.USED_GOOD,
};

// Vinted supports both text strings (server API) and numeric IDs (extension internal API)
const FROM_VINTED = {
  // Text-based (server API — vintedItem.status is descriptive string)
  'new with tags': CONDITIONS.NEW,
  'new without tags': CONDITIONS.LIKE_NEW,
  'very good': CONDITIONS.USED_EXCELLENT,
  'good': CONDITIONS.USED_GOOD,
  'satisfactory': CONDITIONS.USED_ACCEPTABLE,
  // Numeric IDs (extension — vintedItem.status is numeric status_id)
  '6': CONDITIONS.NEW,
  '1': CONDITIONS.LIKE_NEW,
  '2': CONDITIONS.USED_EXCELLENT,
  '3': CONDITIONS.USED_GOOD,
  '4': CONDITIONS.USED_ACCEPTABLE
};

const FROM_EBAY_TRADING = {
  '1000': CONDITIONS.NEW,
  '1500': CONDITIONS.LIKE_NEW,
  '1750': CONDITIONS.NEW_WITH_DEFECTS,
  '2000': CONDITIONS.REFURBISHED,
  '2010': CONDITIONS.REFURBISHED,
  '2020': CONDITIONS.REFURBISHED,
  '2030': CONDITIONS.REFURBISHED,
  '2500': CONDITIONS.REFURBISHED,
  '2750': CONDITIONS.LIKE_NEW,
  '2990': CONDITIONS.USED_EXCELLENT,      // Pre-owned – Excellent (apparel, 2025)
  '3000': CONDITIONS.USED_EXCELLENT,
  '3010': CONDITIONS.USED_ACCEPTABLE,     // Pre-owned – Fair (apparel, 2025)
  '4000': CONDITIONS.USED_VERY_GOOD,
  '5000': CONDITIONS.USED_GOOD,
  '6000': CONDITIONS.USED_ACCEPTABLE,
  '7000': CONDITIONS.FOR_PARTS,
  // eBay display names — stored in ebayAspects.condition when the numeric ID was
  // unmapped (React <option value={null}> causes browser to use text content as value)
  'pre-owned - excellent': CONDITIONS.USED_EXCELLENT,
  'pre-owned \u2013 excellent': CONDITIONS.USED_EXCELLENT,
  'pre-owned - fair': CONDITIONS.USED_ACCEPTABLE,
  'pre-owned \u2013 fair': CONDITIONS.USED_ACCEPTABLE,
  'pre-owned - good': CONDITIONS.USED_GOOD,
  'pre-owned \u2013 good': CONDITIONS.USED_GOOD,
};

const FROM_EBAY_INVENTORY = {
  'new': CONDITIONS.NEW,
  'new_other': CONDITIONS.LIKE_NEW,
  'new_with_defects': CONDITIONS.NEW_WITH_DEFECTS,
  'like_new': CONDITIONS.LIKE_NEW,
  'certified_refurbished': CONDITIONS.REFURBISHED,
  'excellent_refurbished': CONDITIONS.REFURBISHED,
  'very_good_refurbished': CONDITIONS.REFURBISHED,
  'good_refurbished': CONDITIONS.REFURBISHED,
  'seller_refurbished': CONDITIONS.REFURBISHED,
  'pre_owned_excellent': CONDITIONS.USED_EXCELLENT,   // apparel, 2025
  'pre_owned_fair': CONDITIONS.USED_ACCEPTABLE,       // apparel, 2025
  'used_excellent': CONDITIONS.USED_EXCELLENT,
  'used_very_good': CONDITIONS.USED_VERY_GOOD,
  'used_good': CONDITIONS.USED_GOOD,
  'used_acceptable': CONDITIONS.USED_ACCEPTABLE,
  'for_parts_or_not_working': CONDITIONS.FOR_PARTS
};

const FROM_BIGCOMMERCE = {
  'new': CONDITIONS.NEW,
  'used': CONDITIONS.USED_GOOD,
  'refurbished': CONDITIONS.REFURBISHED
};

// Etsy has no condition field; we derive from when_made.
// "made_to_order" = brand new item; all date ranges = pre-existing = used.
const FROM_ETSY = {
  'made_to_order': CONDITIONS.NEW,
  '2020_2026':     CONDITIONS.USED_GOOD,
  '2010_2019':     CONDITIONS.USED_GOOD,
  '2007_2009':     CONDITIONS.USED_GOOD,
  'before_2007':   CONDITIONS.USED_GOOD,
  '2000_2006':     CONDITIONS.USED_GOOD,
  '1990s':         CONDITIONS.USED_GOOD,
  '1980s':         CONDITIONS.USED_GOOD,
  '1970s':         CONDITIONS.USED_GOOD,
  '1960s':         CONDITIONS.USED_GOOD,
  '1950s':         CONDITIONS.USED_GOOD,
  '1940s':         CONDITIONS.USED_GOOD,
  '1930s':         CONDITIONS.USED_GOOD,
  '1920s':         CONDITIONS.USED_GOOD,
  '1910s':         CONDITIONS.USED_GOOD,
  '1900s':         CONDITIONS.USED_GOOD,
  '1800s':         CONDITIONS.USED_GOOD,
  '1700s':         CONDITIONS.USED_GOOD,
  'before_1700':   CONDITIONS.USED_GOOD,
};

// ==================== EXPORT MAPS (Canonical → Platform) ====================

const TO_DEPOP = {
  [CONDITIONS.NEW]: 'brand_new',
  [CONDITIONS.LIKE_NEW]: 'used_like_new',
  [CONDITIONS.NEW_WITH_DEFECTS]: 'used_like_new',
  [CONDITIONS.USED_EXCELLENT]: 'used_excellent',
  [CONDITIONS.USED_VERY_GOOD]: 'used_excellent',
  [CONDITIONS.USED_GOOD]: 'used_good',
  [CONDITIONS.USED_ACCEPTABLE]: 'used_fair',
  [CONDITIONS.REFURBISHED]: 'used_excellent',
  [CONDITIONS.FOR_PARTS]: 'used_fair'
};

// New/Never Worn, Gently Used, Used, Very Worn
const TO_GRAILED = {
  [CONDITIONS.NEW]: 'is_new',
  [CONDITIONS.NEW_WITH_DEFECTS]: 'is_new',
  [CONDITIONS.LIKE_NEW]: 'is_gently_used',
  [CONDITIONS.USED_EXCELLENT]: 'is_gently_used',
  [CONDITIONS.USED_VERY_GOOD]: 'is_used',
  [CONDITIONS.USED_GOOD]: 'is_used',
  [CONDITIONS.REFURBISHED]: 'is_used',
  [CONDITIONS.USED_ACCEPTABLE]: 'is_worn',
  [CONDITIONS.FOR_PARTS]: 'is_worn'
};

// OfferUp's form offers New, Refurbished, Open Box, Used and Broken, and OfferUp asks sellers to round down
// when in doubt: anything worn, opened or flawed is Used, and the seller can pick Open Box themselves.
const TO_OFFERUP = {
  [CONDITIONS.NEW]:              'NEW',
  [CONDITIONS.LIKE_NEW]:         'USED',
  [CONDITIONS.NEW_WITH_DEFECTS]: 'USED',
  [CONDITIONS.USED_EXCELLENT]:   'USED',
  [CONDITIONS.USED_VERY_GOOD]:   'USED',
  [CONDITIONS.USED_GOOD]:        'USED',
  [CONDITIONS.USED_ACCEPTABLE]:  'USED',
  [CONDITIONS.REFURBISHED]:      'REFURBISHED',
  [CONDITIONS.FOR_PARTS]:        'BROKEN'
};

// Vestiaire's reviewers check the condition against the photos, so Voolist rounds down when in doubt, as on
// OfferUp: "New" never claims a tag, "Like New" may have been worn, and anything with a flaw is at most Good.
// The seller can pick a higher grade in the Vestiaire section.
const TO_VESTIAIRE = {
  [CONDITIONS.NEW]:              'Never worn',
  [CONDITIONS.LIKE_NEW]:         'Very good condition',
  [CONDITIONS.NEW_WITH_DEFECTS]: 'Good condition',
  [CONDITIONS.USED_EXCELLENT]:   'Very good condition',
  [CONDITIONS.USED_VERY_GOOD]:   'Very good condition',
  [CONDITIONS.USED_GOOD]:        'Good condition',
  [CONDITIONS.USED_ACCEPTABLE]:  'Fair condition',
  [CONDITIONS.REFURBISHED]:      'Good condition',
  [CONDITIONS.FOR_PARTS]:        'Fair condition'
};

const TO_POSHMARK = {
  [CONDITIONS.NEW]: 'nwt',
  [CONDITIONS.LIKE_NEW]: 'uln',
  [CONDITIONS.NEW_WITH_DEFECTS]: 'uln',
  [CONDITIONS.USED_EXCELLENT]: 'uln',
  [CONDITIONS.USED_VERY_GOOD]: 'ug',
  [CONDITIONS.USED_GOOD]: 'ug',
  [CONDITIONS.USED_ACCEPTABLE]: 'uf',
  [CONDITIONS.REFURBISHED]: 'uln',
  [CONDITIONS.FOR_PARTS]: 'uf'
};

// Vinted export uses numeric ID strings
const TO_VINTED = {
  [CONDITIONS.NEW]: '6',
  [CONDITIONS.LIKE_NEW]: '1',
  [CONDITIONS.NEW_WITH_DEFECTS]: '1',
  [CONDITIONS.USED_EXCELLENT]: '2',
  [CONDITIONS.USED_VERY_GOOD]: '2',
  [CONDITIONS.USED_GOOD]: '3',
  [CONDITIONS.USED_ACCEPTABLE]: '4',
  [CONDITIONS.REFURBISHED]: '2',
  [CONDITIONS.FOR_PARTS]: '4'
};

// eBay Trading API uses numeric string IDs
// Note: Use 1500 (New Other) for LIKE_NEW instead of 2750 (Like New) because
// 2750 is only valid in select categories (e.g. clothing), while 1500 is universally supported.
const TO_EBAY_TRADING = {
  [CONDITIONS.NEW]: '1000',
  [CONDITIONS.LIKE_NEW]: '1500',
  [CONDITIONS.NEW_WITH_DEFECTS]: '1750',
  [CONDITIONS.USED_EXCELLENT]: '3000',
  [CONDITIONS.USED_VERY_GOOD]: '4000',
  [CONDITIONS.USED_GOOD]: '5000',
  [CONDITIONS.USED_ACCEPTABLE]: '6000',
  [CONDITIONS.REFURBISHED]: '2500',
  [CONDITIONS.FOR_PARTS]: '7000'
};

// eBay Inventory API uses enum strings
const TO_EBAY_INVENTORY = {
  [CONDITIONS.NEW]: 'NEW',
  [CONDITIONS.LIKE_NEW]: 'LIKE_NEW',
  [CONDITIONS.NEW_WITH_DEFECTS]: 'NEW_WITH_DEFECTS',
  [CONDITIONS.USED_EXCELLENT]: 'USED_EXCELLENT',
  [CONDITIONS.USED_VERY_GOOD]: 'USED_VERY_GOOD',
  [CONDITIONS.USED_GOOD]: 'USED_GOOD',
  [CONDITIONS.USED_ACCEPTABLE]: 'USED_ACCEPTABLE',
  [CONDITIONS.REFURBISHED]: 'SELLER_REFURBISHED',
  [CONDITIONS.FOR_PARTS]: 'FOR_PARTS_OR_NOT_WORKING'
};

const TO_BIGCOMMERCE = {
  [CONDITIONS.NEW]: 'New',
  [CONDITIONS.LIKE_NEW]: 'New',
  [CONDITIONS.NEW_WITH_DEFECTS]: 'Used',
  [CONDITIONS.USED_EXCELLENT]: 'Used',
  [CONDITIONS.USED_VERY_GOOD]: 'Used',
  [CONDITIONS.USED_GOOD]: 'Used',
  [CONDITIONS.USED_ACCEPTABLE]: 'Used',
  [CONDITIONS.REFURBISHED]: 'Refurbished',
  [CONDITIONS.FOR_PARTS]: 'Used'
};

// Canonical → Etsy when_made (best-effort: NEW = made to order, used = recent date range)
const TO_ETSY = {
  [CONDITIONS.NEW]:              'made_to_order',
  [CONDITIONS.LIKE_NEW]:         '2020_2026',
  [CONDITIONS.NEW_WITH_DEFECTS]: '2020_2026',
  [CONDITIONS.USED_EXCELLENT]:   '2020_2026',
  [CONDITIONS.USED_VERY_GOOD]:   '2020_2026',
  [CONDITIONS.USED_GOOD]:        '2020_2026',
  [CONDITIONS.USED_ACCEPTABLE]:  '2020_2026',
  [CONDITIONS.REFURBISHED]:      '2020_2026',
  [CONDITIONS.FOR_PARTS]:        '2020_2026',
};

// ==================== LOOKUP TABLES ====================

const FROM_MAP = {
  [PLATFORMS.DEPOP]: FROM_DEPOP,
  [PLATFORMS.GRAILED]: FROM_GRAILED,
  [PLATFORMS.OFFERUP]: FROM_OFFERUP,
  [PLATFORMS.VESTIAIRE]: FROM_VESTIAIRE,
  [PLATFORMS.POSHMARK]: FROM_POSHMARK,
  [PLATFORMS.VINTED]: FROM_VINTED,
  [PLATFORMS.EBAY_TRADING]: FROM_EBAY_TRADING,
  [PLATFORMS.EBAY_INVENTORY]: FROM_EBAY_INVENTORY,
  [PLATFORMS.BIGCOMMERCE]: FROM_BIGCOMMERCE,
  [PLATFORMS.ETSY]: FROM_ETSY
};

const TO_MAP = {
  [PLATFORMS.DEPOP]: TO_DEPOP,
  [PLATFORMS.GRAILED]: TO_GRAILED,
  [PLATFORMS.OFFERUP]: TO_OFFERUP,
  [PLATFORMS.VESTIAIRE]: TO_VESTIAIRE,
  [PLATFORMS.POSHMARK]: TO_POSHMARK,
  [PLATFORMS.VINTED]: TO_VINTED,
  [PLATFORMS.EBAY_TRADING]: TO_EBAY_TRADING,
  [PLATFORMS.EBAY_INVENTORY]: TO_EBAY_INVENTORY,
  [PLATFORMS.BIGCOMMERCE]: TO_BIGCOMMERCE,
  [PLATFORMS.ETSY]: TO_ETSY
};

// Platform-specific defaults used when no mapping is found
const PLATFORM_DEFAULTS = {
  [PLATFORMS.DEPOP]: 'used_good',
  [PLATFORMS.GRAILED]: 'is_used',
  [PLATFORMS.OFFERUP]: 'USED',
  [PLATFORMS.VESTIAIRE]: 'Good condition',
  [PLATFORMS.POSHMARK]: 'ug',
  [PLATFORMS.VINTED]: '3',
  [PLATFORMS.EBAY_TRADING]: '5000',
  [PLATFORMS.EBAY_INVENTORY]: 'USED_GOOD',
  [PLATFORMS.BIGCOMMERCE]: 'Used',
  [PLATFORMS.ETSY]: '2020_2026'
};

// ==================== PUBLIC API ====================

/**
 * Convert a platform-specific condition value to a canonical Voolist condition.
 * Input is normalized (lowercased + trimmed) before lookup.
 * @param {string} platformValue - Platform-specific condition value
 * @param {string} platform - One of PLATFORMS.*
 * @returns {string|null} Canonical CONDITIONS.* value or null
 */
export const fromCondition = (platformValue, platform) => {
  if (platformValue == null) return null;

  if (NO_CONDITION_PLATFORMS.has(platform)) {
    return null;
  }

  const table = FROM_MAP[platform];
  if (!table) {
    return null;
  }

  const normalized = String(platformValue).trim().toLowerCase();
  const result = table[normalized];

  if (!result) {
    return null;
  }

  return result;
};

/**
 * Convert a canonical Voolist condition to a platform-specific value.
 * Returns the correct type for each platform:
 *   EBAY_TRADING   → "1000" (numeric string ID)
 *   EBAY_INVENTORY → "NEW"  (enum string)
 *   DEPOP          → "brand_new" (lowercase)
 *   POSHMARK       → "nwt"  (code)
 *   VINTED         → "6"    (numeric string ID)
 *   BIGCOMMERCE    → "New"  (capitalized)
 * @param {string} voolistValue - Canonical CONDITIONS.* value
 * @param {string} platform - One of PLATFORMS.*
 * @returns {string|null} Platform-specific condition string or null
 */
export const toCondition = (voolistValue, platform) => {
  if (NO_CONDITION_PLATFORMS.has(platform)) {
    return null;
  }

  const table = TO_MAP[platform];
  if (!table) {
    return null;
  }

  const result = table[voolistValue];

  if (result === undefined) {
    return PLATFORM_DEFAULTS[platform] || null;
  }

  return result;
};

/**
 * Check if a value is a valid canonical Voolist condition.
 * @param {string} value
 * @returns {boolean}
 */
export const isValidCondition = (value) => Object.values(CONDITIONS).includes(value);

/**
 * Get the platform-safe default condition string.
 * @param {string} platform - One of PLATFORMS.*
 * @returns {string|null}
 */
export const getDefaultCondition = (platform) => PLATFORM_DEFAULTS[platform] || null;

// ==================== WHATNOT (an answer per attribute) ====================
// Keep in sync with server/src/utils/condition-mapper.js (compared in whatnot-mirrors.test.js).

// On Whatnot the condition is an attribute of the item's category (utils/whatnot-taxonomy.js), and
// categories ask it in different words. Each family below covers the condition attributes that share one
// set of words: for every Voolist condition, the answers in order of preference. The first one the
// attribute offers is sent, spelled the way that attribute spells it (the outdoor-gear attributes write
// "pre-owned - good"); a condition none of them fits leaves the question for the seller.
//
// Not pre-filled at all: the attributes that grade a collectible (trading and sports cards, comics,
// coins, paper money, records and CDs, video games, memorabilia, "Mint" toys, LEGO's box states, food).
// Their answer is the seller's judgement of that one item, which Voolist's condition cannot express.
export const WHATNOT_CONDITION_FAMILIES = [
  {
    // Clothing and shoes in the fashion and kids' categories
    attributeIds: [73],
    answers: {
      NEW: ['New With Tags'], LIKE_NEW: ['New without Tags'], NEW_WITH_DEFECTS: ['New with Defects'],
      USED_EXCELLENT: ['Pre-owned - Excellent'], USED_VERY_GOOD: ['Pre-owned - Good'], USED_GOOD: ['Pre-owned - Good'],
      USED_ACCEPTABLE: ['Pre-owned - Fair'], REFURBISHED: ['Pre-owned - Excellent'], FOR_PARTS: ['Pre-owned - Damaged'],
    },
  },
  {
    // Sneakers
    attributeIds: [2],
    answers: {
      NEW: ['New'], NEW_WITH_DEFECTS: ['New With Defects'], LIKE_NEW: ['Used'], USED_EXCELLENT: ['Used'],
      USED_VERY_GOOD: ['Used'], USED_GOOD: ['Used'], USED_ACCEPTABLE: ['Used'], REFURBISHED: ['Used'], FOR_PARTS: ['Used'],
    },
  },
  {
    // New or used: plush toys, Disney pins, golf
    attributeIds: [550, 830, 1010, 1012, 1013, 1018, 1021, 1023],
    answers: {
      NEW: ['New'], NEW_WITH_DEFECTS: ['New'], LIKE_NEW: ['Used'], USED_EXCELLENT: ['Used'], USED_VERY_GOOD: ['Used'],
      USED_GOOD: ['Used'], USED_ACCEPTABLE: ['Used'], REFURBISHED: ['Used'], FOR_PARTS: ['Used'],
    },
  },
  {
    // Pre-owned grades: outdoor gear, craft supplies, jewelry, antiques (which offer no "New")
    attributeIds: [13492, 13493, 67429, 67430, 13486, 11639, 13491],
    answers: {
      NEW: ['New'], NEW_WITH_DEFECTS: ['New with defects', 'New'], LIKE_NEW: ['Pre-owned - Excellent'],
      USED_EXCELLENT: ['Pre-owned - Excellent'], USED_VERY_GOOD: ['Pre-owned - Good'], USED_GOOD: ['Pre-owned - Good'],
      USED_ACCEPTABLE: ['Pre-owned - Fair'], REFURBISHED: ['Pre-owned - Excellent'], FOR_PARTS: ['Pre-owned - Damaged'],
    },
  },
  {
    // Books, movies, art and prints
    attributeIds: [415, 836, 841],
    answers: {
      NEW: ['Brand New'], LIKE_NEW: ['Like New'], NEW_WITH_DEFECTS: ['Like New'], USED_EXCELLENT: ['Very Good'],
      USED_VERY_GOOD: ['Very Good'], USED_GOOD: ['Good'], USED_ACCEPTABLE: ['Fair'], REFURBISHED: ['Very Good'], FOR_PARTS: ['Poor'],
    },
  },
  {
    // Designer, midrange and other bags and accessories
    attributeIds: [91, 49268, 559],
    answers: {
      NEW: ['New With Tags'], LIKE_NEW: ['New Without Tags'], NEW_WITH_DEFECTS: ['New With Defects'],
      USED_EXCELLENT: ['Excellent'], USED_VERY_GOOD: ['Very Good'], USED_GOOD: ['Good'], USED_ACCEPTABLE: ['Fair'],
      REFURBISHED: ['Excellent'], FOR_PARTS: ['Poor'],
    },
  },
  {
    // Sunglasses and eyewear: the bags' grades, written with "Pre-owned"
    attributeIds: [49263],
    answers: {
      NEW: ['New With Tags'], LIKE_NEW: ['New Without Tags'], NEW_WITH_DEFECTS: ['New With Defects'],
      USED_EXCELLENT: ['Pre-owned - Excellent'], USED_VERY_GOOD: ['Pre-owned - Very Good'], USED_GOOD: ['Pre-owned - Good'],
      USED_ACCEPTABLE: ['Pre-owned - Fair'], REFURBISHED: ['Pre-owned - Excellent'], FOR_PARTS: ['Pre-owned - Poor'],
    },
  },
  {
    // Home and garden, hunting (which offers no "Refurbished")
    attributeIds: [13494, 13495],
    answers: {
      NEW: ['New with box'], LIKE_NEW: ['New without box'], NEW_WITH_DEFECTS: ['New with defects'],
      USED_EXCELLENT: ['Pre-owned - Good'], USED_VERY_GOOD: ['Pre-owned - Good'], USED_GOOD: ['Pre-owned - Good'],
      USED_ACCEPTABLE: ['Pre-owned - Fair'], REFURBISHED: ['Refurbished'], FOR_PARTS: ['Pre-owned - Damaged'],
    },
  },
  {
    // Watches: no answer for one sold for parts
    attributeIds: [158336],
    answers: {
      NEW: ['New'], LIKE_NEW: ['Like New'], NEW_WITH_DEFECTS: ['Like New'], USED_EXCELLENT: ['Very Good'],
      USED_VERY_GOOD: ['Very Good'], USED_GOOD: ['Good'], USED_ACCEPTABLE: ['Fair'], REFURBISHED: ['Very Good'],
    },
  },
  {
    // Electronics
    attributeIds: [208],
    answers: {
      NEW: ['Brand New'], LIKE_NEW: ['Open-box'], NEW_WITH_DEFECTS: ['Open-box'],
      USED_EXCELLENT: ['Used and fully functioning'], USED_VERY_GOOD: ['Used and fully functioning'],
      USED_GOOD: ['Used and fully functioning'], USED_ACCEPTABLE: ['Used and fully functioning'],
      REFURBISHED: ['Refurbished - Good'], FOR_PARTS: ['For parts, not fully functioning, or untested'],
    },
    // Answers no Voolist condition is sent as, read back all the same.
    alsoReads: {
      'Refurbished - Excellent': CONDITIONS.REFURBISHED,
      'Refurbished - Very Good': CONDITIONS.REFURBISHED,
      'Refurbished - Fair': CONDITIONS.REFURBISHED,
    },
  },
];

const WHATNOT_CONDITION_FAMILY_OF = new Map(WHATNOT_CONDITION_FAMILIES
  .flatMap((family) => family.attributeIds.map((attributeId) => [attributeId, family])));

// An answer several Voolist conditions are sent as reads back as the first of them in this order: the
// plainest reading of it ("Used" is a used item in good condition, "Like New" is like new).
const WHATNOT_READ_BACK_ORDER = [
  CONDITIONS.NEW, CONDITIONS.USED_GOOD, CONDITIONS.USED_VERY_GOOD, CONDITIONS.USED_EXCELLENT,
  CONDITIONS.USED_ACCEPTABLE, CONDITIONS.FOR_PARTS, CONDITIONS.LIKE_NEW, CONDITIONS.NEW_WITH_DEFECTS,
  CONDITIONS.REFURBISHED,
];

const whatnotSpelling = (attribute, answer) => (attribute.values || [])
  .find((own) => own.toLowerCase() === String(answer).trim().toLowerCase()) || null;

/**
 * The answer a Whatnot condition attribute is sent for a Voolist condition, spelled as the attribute
 * spells it; null when the attribute is not one Voolist pre-fills or offers no fitting answer.
 *
 * @param {string} voolistValue - Canonical CONDITIONS.* value
 * @param {{ id: number, values?: string[] }|null} attribute - The category's condition attribute
 * @returns {string|null}
 */
export const toWhatnotCondition = (voolistValue, attribute) => {
  const family = attribute && WHATNOT_CONDITION_FAMILY_OF.get(Number(attribute.id));
  if (!family || voolistValue == null) return null;
  return (family.answers[voolistValue] || []).map((answer) => whatnotSpelling(attribute, answer)).find(Boolean) || null;
};

/**
 * The Voolist condition a Whatnot condition answer reads as; null for an answer of a grading scale, or
 * one no Voolist condition fits ("Not Specified").
 *
 * @param {string} whatnotValue - The answer as Whatnot holds it
 * @param {{ id: number, values?: string[] }|null} attribute - The category's condition attribute
 * @returns {string|null}
 */
export const fromWhatnotCondition = (whatnotValue, attribute) => {
  const family = attribute && WHATNOT_CONDITION_FAMILY_OF.get(Number(attribute.id));
  if (!family || whatnotValue == null) return null;
  const read = String(whatnotValue).trim().toLowerCase();
  const sentAs = WHATNOT_READ_BACK_ORDER.find((condition) => toWhatnotCondition(condition, attribute)?.toLowerCase() === read);
  if (sentAs) return sentAs;
  const [, alsoRead] = Object.entries(family.alsoReads || {}).find(([answer]) => answer.toLowerCase() === read) || [];
  return alsoRead || null;
};
